import { extrairDadosDocumento } from './leituraDocumento';
import { extrairVeiculoDeItens, extrairVeiculoDeTexto } from './leituraVeiculo';

// Leitura de documento (OCR) 100% local e gratuita: Tesseract.js roda no navegador, com os arquivos
// do motor e o modelo em português servidos de /public/ocr. A imagem nunca sai da máquina do usuário.
const BASE = `${window.location.origin}${process.env.PUBLIC_URL || ''}/ocr`;
const MAX_PAGINAS_PDF = 3;
const LARGURA_MAXIMA_PX = 3200;

// A barra de progresso cobre todas as etapas, e não só o reconhecimento do texto, para nunca parecer travada:
//   0-15%  preparar o PDF (só para PDF)      15-35%  carregar o leitor e o modelo em português
//   35-100%  reconhecer o texto de cada página
const FIM_PDF = 15;
const FIM_PREPARO = 35;

async function pdfParaImagens(arquivo, onEtapa) {
  const pdfjs = await import('pdfjs-dist/es5/build/pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = `${BASE}/pdf.worker.min.js`;
  const pdf = await pdfjs.getDocument({ data: await arquivo.arrayBuffer() }).promise;
  const totalPaginas = Math.min(pdf.numPages, MAX_PAGINAS_PDF);

  const imagens = [];
  for (let i = 1; i <= totalPaginas; i++) {
    onEtapa((FIM_PDF * (i - 1)) / totalPaginas, `Preparando o PDF (página ${i} de ${totalPaginas})...`);
    const pagina = await pdf.getPage(i);
    // Resolução alta: no PDF da CNH digital o cartão ocupa só parte da página, e com escala menor o OCR erra letras.
    // O limite de largura evita canvas gigantes em PDFs de página grande.
    const escala = Math.min(4, LARGURA_MAXIMA_PX / pagina.getViewport({ scale: 1 }).width);
    const viewport = pagina.getViewport({ scale: escala });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await pagina.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    imagens.push(canvas);
  }
  return imagens;
}

// O percentual nunca volta para trás, mesmo que o leitor reporte etapas fora de ordem
function criarAvisador(onProgresso) {
  let maximo = 0;
  return (percentual, mensagem) => {
    maximo = Math.max(maximo, Math.min(100, Math.round(percentual)));
    onProgresso(maximo, mensagem);
  };
}

// OCR de um JPG, PNG ou PDF: devolve o texto lido. Cuida da barra de progresso de 0 a 100%.
async function lerTextoPorOcr(arquivo, avisar) {
  avisar(1, 'Iniciando...');
  const { createWorker } = await import('tesseract.js');
  const eraPdf = arquivo.type === 'application/pdf';
  const imagens = eraPdf ? await pdfParaImagens(arquivo, avisar) : [arquivo];
  const inicioPreparo = eraPdf ? FIM_PDF : 3;
  const faixaPreparo = FIM_PREPARO - inicioPreparo;

  let indice = 0;
  const worker = createWorker({
    workerPath: `${BASE}/worker.min.js`,
    corePath: `${BASE}/tesseract-core.wasm.js`,
    langPath: BASE,
    logger: (m) => {
      // o motor informa cada etapa do preparo e depois o reconhecimento; a primeira vez baixa o modelo
      if (m.status === 'loading tesseract core') {
        avisar(inicioPreparo + m.progress * faixaPreparo * 0.4, 'Carregando o leitor de documentos...');
      } else if (m.status === 'loading language traineddata') {
        avisar(inicioPreparo + faixaPreparo * (0.4 + m.progress * 0.5), 'Carregando o modelo em português...');
      } else if (m.status === 'initializing api') {
        avisar(inicioPreparo + faixaPreparo * 0.95, 'Preparando a leitura...');
      } else if (m.status === 'recognizing text') {
        const fracao = (indice + m.progress) / imagens.length;
        avisar(FIM_PREPARO + fracao * (100 - FIM_PREPARO), 'Lendo o texto do documento...');
      }
    },
  });

  try {
    avisar(inicioPreparo, 'Carregando o leitor de documentos...');
    await worker.load();
    await worker.loadLanguage('por');
    await worker.initialize('por');
    avisar(FIM_PREPARO, 'Lendo o texto do documento...');

    let texto = '';
    for (indice = 0; indice < imagens.length; indice++) {
      const { data } = await worker.recognize(imagens[indice]);
      texto += `\n${data.text}`;
    }
    avisar(100, 'Concluído');
    return texto;
  } finally {
    await worker.terminate();
  }
}

// Lê um documento de pessoa (CNH, RG) em JPG, PNG ou PDF e devolve os campos do cadastro encontrados:
// { nome, cpfCnpj, rg }. onProgresso(percentual de 0 a 100, mensagem da etapa) é chamado ao longo do processo.
export async function lerDocumento(arquivo, onProgresso = () => {}) {
  const texto = await lerTextoPorOcr(arquivo, criarAvisador(onProgresso));
  return extrairDadosDocumento(texto);
}

// Texto do PDF com a posição de cada pedaço, quando o PDF é digital (como o CRLV-e). Vazio se for só imagem.
async function itensDeTextoDoPdf(arquivo) {
  const pdfjs = await import('pdfjs-dist/es5/build/pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = `${BASE}/pdf.worker.min.js`;
  const pdf = await pdfjs.getDocument({ data: await arquivo.arrayBuffer() }).promise;
  const paginas = [];
  for (let i = 1; i <= Math.min(pdf.numPages, MAX_PAGINAS_PDF); i++) {
    const conteudo = await (await pdf.getPage(i)).getTextContent();
    paginas.push(conteudo.items.map((t) => ({ str: t.str, x: t.transform[4], y: t.transform[5], w: t.width, h: t.height })));
  }
  return paginas;
}

// Lê um documento de veículo (CRLV, ATPV) e devolve { dados: { placa, renavam, chassi, motor, marcaModelo, cor, ano,
// proprietario }, duvidas }. "proprietario" ({ nome, cpfCnpj }) só vem do CRLV: ver leituraVeiculo.js.
// PDF digital (CRLV-e) é lido direto pelo texto, na hora e sem erro de letra; foto ou PDF escaneado passa por OCR.
export async function lerDocumentoVeiculo(arquivo, onProgresso = () => {}) {
  const avisar = criarAvisador(onProgresso);

  if (arquivo.type === 'application/pdf') {
    avisar(1, 'Lendo o PDF...');
    try {
      for (const itens of await itensDeTextoDoPdf(arquivo)) {
        const resultado = extrairVeiculoDeItens(itens);
        if (resultado.dados.placa || resultado.dados.chassi) {
          avisar(100, 'Concluído');
          return resultado;
        }
      }
    } catch (erro) {
      console.error('Erro ao ler o texto do PDF; tentando por OCR:', erro);
    }
  }

  const texto = await lerTextoPorOcr(arquivo, avisar);
  return extrairVeiculoDeTexto(texto);
}
